import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { CreatePlanRequest } from '../types/plan.types';

/**
 * Default value for a new plan entry.
 * Required fields (orderStatus, placementDate, quantityUnit, formulationProductType, vehicleSize) are left empty for user to fill.
 */
export const defaultPlanValue: CreatePlanRequest = {
  orderStatus: null,
  customerSupplierId: null,
  placementDate: '',
  timeHope: null,
  timePossible: '',
  timePossibleRemarks: null,
  placeToPlace: '',
  quantity: null,
  quantitySuffix: null,
  quantityUnit: null,
  formulationProductType: null,
  formulationProduct: '',
  vehicleSize: null,
  precedingMaterialType: null,
  precedingMaterialQuantity: null,
  precedingMaterialUnit: null,
  pitchHope: null,
  pitchPossible: null,
  placingMethod: null,
  placingMethodOthers: null,
  witnessCompanyId: null,
  witnessName: '',
  witnessTel: '',
  remarksForClient: null,
  remarks: null,
  id: null,
  isArchive: false,
};

/**
 * Store interface for managing plan table data
 */
interface CreatePlanTableStore {
  // State
  createPlanTable: CreatePlanRequest[];
  defaultPlanTable: CreatePlanRequest[];
  // Actions
  setCreatePlanTable: (plans: CreatePlanRequest[]) => void;
  addPlan: (plan?: CreatePlanRequest) => void;
  addPlanAbove: (index: number, plan?: CreatePlanRequest) => void;
  addPlanBelow: (index: number, plan?: CreatePlanRequest) => void;
  updatePlan: (index: number, plan: Partial<CreatePlanRequest>) => void;
  deletePlan: (index: number) => void;
  resetPlanTable: () => void;
  getPlan: (index: number) => CreatePlanRequest | undefined;
}

/**
 * Create Plan Table Store
 * Manages the state of plan entries in the registration table
 */
export const useCreatePlanTableStore = create<CreatePlanTableStore>()(
  devtools(
    (set, get) => ({
      // Initial state - start with 10 empty rows
      createPlanTable: Array.from({ length: 10 }, () => ({ ...defaultPlanValue })),
      defaultPlanTable: Array.from({ length: 10 }, () => ({ ...defaultPlanValue })),
      // Set entire table
      setCreatePlanTable: (plans) => {
        set({ createPlanTable: plans }, false, 'setCreatePlanTable');
        console.log('Plan table updated:', plans);
      },

      // Add new plan at the end
      addPlan: (plan) => {
        const newPlan = plan || { ...defaultPlanValue };
        set(
          (state) => ({
            createPlanTable: [...state.createPlanTable, newPlan],
          }),
          false,
          'addPlan'
        );
      },

      // Add plan above specified index
      addPlanAbove: (index, plan) => {
        const newPlan = plan || { ...defaultPlanValue };
        set(
          (state) => {
            const newTable = [...state.createPlanTable];
            newTable.splice(index, 0, newPlan);
            return { createPlanTable: newTable };
          },
          false,
          'addPlanAbove'
        );
      },

      // Add plan below specified index
      addPlanBelow: (index, plan) => {
        const newPlan = plan || { ...defaultPlanValue };
        set(
          (state) => {
            const newTable = [...state.createPlanTable];
            newTable.splice(index + 1, 0, newPlan);
            return { createPlanTable: newTable };
          },
          false,
          'addPlanBelow'
        );
      },

      // Update plan at specified index
      updatePlan: (index, plan) => {
        set(
          (state) => {
            const newTable = [...state.createPlanTable];
            if (newTable[index]) {
              newTable[index] = { ...newTable[index], ...plan };
            }
            return { createPlanTable: newTable };
          },
          false,
          'updatePlan'
        );
        const updated = get().createPlanTable;
        console.log('Plan updated at index', index, ':', updated[index]);
      },

      // Delete plan at specified index
      deletePlan: (index) => {
        set(
          (state) => {
            const newTable = state.createPlanTable.filter((_, i) => i !== index);
            // Ensure at least one row exists
            if (newTable.length === 0) {
              newTable.push({ ...defaultPlanValue });
            }
            return { createPlanTable: newTable };
          },
          false,
          'deletePlan'
        );
      },

      // Reset table to initial state (10 rows as default)
      resetPlanTable: () => {
        set(
          { createPlanTable: Array.from({ length: 10 }, () => ({ ...defaultPlanValue })) },
          false,
          'resetPlanTable'
        );
        console.log('Plan table reset to initial state');
      },

      // Get plan at specified index
      getPlan: (index) => {
        const { createPlanTable } = get();
        return createPlanTable[index];
      },
    }),
    {
      name: 'create-plan-table-store',
    }
  )
);
